import { usePopularImages } from "@features/popular/use-popular";
import { useEffectPlease } from "@features/utils";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnhancedScrollArea } from "./enhanced-scroll-area";
import { TitleSearchHeading } from "./title-search";

export const PopularImages = ({
  type,
  onChange,
}: {
  type: "image_outfit" | "image_hairstyle" | "image_image";
  onChange: (item: { url: string }) => void;
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePopularImages(type, search);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [, setColumnWidth] = useState(100);

  useEffect(() => {
    const updateColumnWidth = () => {
      if (gridRef.current) {
        const computedStyle = window.getComputedStyle(gridRef.current);
        const columnWidthValue = parseFloat(
          computedStyle.getPropertyValue("grid-template-columns").split(" ")[0]
        );
        setColumnWidth(columnWidthValue || 100);
      }
    };

    updateColumnWidth();
    window.addEventListener("resize", updateColumnWidth);

    return () => {
      window.removeEventListener("resize", updateColumnWidth);
    };
  }, []);

  useEffectPlease(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "0px", threshold: 1.0 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="space-y-4">
      <TitleSearchHeading
        title={t("camera.form.image.popular")}
        setQuery={(query) => {
          setSearch(query);
        }}
      />
      <EnhancedScrollArea className="rounded-md overflow-hidden">
        <div className="flex items-start overflow-visible h-80 relative">
          {data?.pages.map((page) =>
            page.map((item) => {
              return (
                <div
                  key={item.url}
                  className="relative cursor-pointer group/popular-image w-auto inline-block shrink-0 h-full"
                  onClick={() => onChange(item)}
                >
                  <img
                    src={item.url}
                    alt={item.name}
                    className="w-auto h-full object-cover"
                  />
                </div>
              );
            })
          )}
          <div ref={observerRef} className="h-1" />
        </div>
      </EnhancedScrollArea>
    </div>
  );
};
